import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    FiCheck,
    FiLock,
    FiMessageSquare,
    FiPhone,
    FiSend,
    FiUser,
} from 'react-icons/fi';
import { MdStar, MdStarBorder } from 'react-icons/md';

import {
    getClinicSurveyInvitation,
    getClinicSurveyForm,
    submitClinicSurvey,
    submitClinicSurveyInvitation,
} from '../api/routes';
import { normalizeSurveyForm, buildSurveyAnswers } from '../utils/surveyForm';




const surveyMeta = {
    admission: {
        legend: 'پذیرش',
    },

    center: {
        legend: 'خدمات مرکز',
    },

    report: {
        legend: 'ریپورت',
    },

    result_delivery: {
        legend: 'جواب‌دهی',
    },

    technologist: {
        legend: 'عملکرد کارشناس',
    },
};


const ratingLabels = {
    1: 'خیلی ضعیف',
    2: 'ضعیف',
    3: 'متوسط',
    4: 'خوب',
    5: 'عالی',
};


function getErrorMessage(error) {
    return (
        error?.response?.data?.message ||
        error?.message ||
        'خطایی رخ داد. دوباره تلاش کنید.'
    );
}


export default function SurveyForm({ kind }) {
    const [searchParams] = useSearchParams();

    const inviteToken = useMemo(
        () => (searchParams.get('t') || '').trim(),
        [searchParams]
    );

    const receptionFromUrl = useMemo(
        () =>
            (
                searchParams.get('reception') ||
                searchParams.get('reception_number') ||
                searchParams.get('code') ||
                ''
            ).trim(),
        [searchParams]
    );

    const phoneFromUrl = useMemo(
        () =>
            (
                searchParams.get('phone') ||
                searchParams.get('phone_number') ||
                searchParams.get('mobile') ||
                ''
            )
                .replace(/\D/g, '')
                .slice(0, 11),
        [searchParams]
    );


    const source = `${kind || 'invitation'}:${inviteToken}:${receptionFromUrl}:${phoneFromUrl}`;
    const currentSource = useRef(source);
    currentSource.current = source;
    const submissionSource = useRef(null);
    const [loadedForm, setLoadedForm] = useState(null);
    const [reload, setReload] = useState(0);
    const form = loadedForm?.source === source ? loadedForm.definition : null;
    const criteria = form?.sections || [];
    const meta = {
        title: form?.title || 'نظرسنجی',
        legend: surveyMeta[form?.survey_kind]?.legend || 'ارزیابی خدمات',
    };


    const [receptionNumber, setReceptionNumber] =
        useState(receptionFromUrl);

    const [phoneNumber, setPhoneNumber] =
        useState(phoneFromUrl);

    const [ratings, setRatings] =
        useState({});

    const [description, setDescription] =
        useState('');

    const [submitting, setSubmitting] =
        useState(false);

    const [error, setError] =
        useState('');

    const [success, setSuccess] =
        useState(false);

    const [inviteLoading, setInviteLoading] =
        useState(true);

    const [inviteError, setInviteError] =
        useState('');


    /*
     * Survey progress
     */
    const answeredCount = criteria.filter(
        item => ratings[item.key]
    ).length;

    const progress =
        criteria.length > 0
            ? Math.round(
                (answeredCount / criteria.length) * 100
            )
            : 0;

    const allRated =
        criteria.length > 0 &&
        answeredCount === criteria.length;


    /*
     * Load the invitation snapshot or the active clinic form
     */
    useEffect(() => {
        let active = true;

        setInviteLoading(true);
        setInviteError('');
        setLoadedForm(null);
        setRatings({});
        setDescription('');
        setError('');
        setSuccess(false);
        setSubmitting(false);
        submissionSource.current = null;
        setReceptionNumber(receptionFromUrl);
        setPhoneNumber(phoneFromUrl);

        const request = inviteToken
            ? getClinicSurveyInvitation(inviteToken)
            : kind ? getClinicSurveyForm(kind) : Promise.reject(new Error('لینک دعوت نظرسنجی ارسال نشده است.'));
        request
            .then(invitation => {
                if (!active) {
                    return;
                }

                const definition = normalizeSurveyForm(invitation, kind);
                setLoadedForm({ source, definition });

                if (inviteToken) {
                    setReceptionNumber(String(invitation.reception_number || ''));
                    setPhoneNumber(String(invitation.masked_phone_number || ''));
                }
            })
            .catch(requestError => {
                if (!active) {
                    return;
                }

                setInviteError(
                    getErrorMessage(requestError)
                );
            })
            .finally(() => {
                if (active) {
                    setInviteLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [inviteToken, kind, source, reload, receptionFromUrl, phoneFromUrl]);


    /*
     * Validate patient identity
     */
    const validateIdentity = () => {
        if (inviteLoading) return 'در حال دریافت فرم نظرسنجی هستیم.';
        if (inviteError) return inviteError;
        if (!form) return 'فرم نظرسنجی دریافت نشده است.';
        if (inviteToken) {
            return '';
        }

        if (!receptionNumber.trim()) {
            return 'شماره پذیرش را وارد کنید.';
        }

        if (!/^09\d{9}$/.test(phoneNumber)) {
            return 'شماره موبایل باید ۱۱ رقم و با 09 شروع شود.';
        }

        return '';
    };


    /*
     * Select rating
     */
    const selectRating = (key, value) => {
        setRatings(current => ({
            ...current,
            [key]: value,
        }));

        if (error) {
            setError('');
        }
    };


    /*
     * Submit survey
     */
    const submit = async event => {
        event.preventDefault();
        if (submitting || submissionSource.current === source) return;

        const validationError =
            validateIdentity();

        if (validationError) {
            setError(validationError);
            return;
        }

        if (!allRated) {
            setError(
                'لطفاً به تمام موارد نظرسنجی امتیاز دهید.'
            );
            return;
        }

        setError('');
        setSubmitting(true);
        submissionSource.current = source;

        try {
            const surveyData = {
                answers: buildSurveyAnswers(criteria, ratings),

                description:
                    description.trim() || null,
            };


            if (inviteToken) {
                await submitClinicSurveyInvitation({
                    ...surveyData,
                    invite_token: inviteToken,
                });
            } else {
                await submitClinicSurvey({
                    ...surveyData,

                    reception_number:
                        receptionNumber.trim(),

                    phone_number:
                        phoneNumber,

                    survey_kind:
                        form.survey_kind,
                    survey_version: form.version,
                });
            }

            if (currentSource.current !== source) return;
            setSuccess(true);

            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        } catch (submitError) {
            if (currentSource.current !== source) return;
            setError(
                getErrorMessage(submitError)
            );
        } finally {
            if (currentSource.current === source) {
                setSubmitting(false);
                submissionSource.current = null;
            }
        }
    };


    /*
     * Success
     */
    if (success && form) {
        return (
            <main className="survey-page">

                <section className="survey-shell survey-success">

                    <span className="survey-success-icon">
                        <FiCheck />
                    </span>

                    <p className="survey-eyebrow">
                        با موفقیت ثبت شد
                    </p>

                    <h1>
                        ممنون از نظر شما
                    </h1>

                    <p>
                        نظر شما با موفقیت ثبت شد.
                        از اینکه برای بهتر شدن خدمات
                        کلینیک تصویربرداری عارف به ما
                        کمک کردید، سپاسگزاریم.
                    </p>

                    <Link
                        className="survey-secondary-button"
                        to="/"
                    >
                        بازگشت به صفحه اصلی
                    </Link>

                </section>

            </main>
        );
    }


    /*
     * Invitation loading/error
     */
    if (inviteLoading || inviteError || !form) {
        return (
            <main className="survey-page">

                <section className="survey-shell survey-success">

                    {inviteLoading ? (
                        <span className="survey-success-icon">
                            <FiLock />
                        </span>
                    ) : (
                        <span
                            className="survey-success-icon"
                            style={{
                                background: '#ef6b78',
                            }}
                        >
                            <FiLock />
                        </span>
                    )}

                    <h1>
                        {inviteLoading
                            ? 'در حال دریافت نظرسنجی'
                            : 'فرم قابل نمایش نیست'
                        }
                    </h1>

                    <p>
                        {inviteLoading
                            ? 'لطفاً چند لحظه صبر کنید.'
                            : inviteError
                        }
                    </p>
                    {!inviteLoading && <button type="button" className="survey-secondary-button"
                        onClick={() => setReload(value => value + 1)}>تلاش دوباره</button>}

                </section>

            </main>
        );
    }


    return (
        <main className="survey-page">

            <div className="survey-glow survey-glow-one" />
            <div className="survey-glow survey-glow-two" />


            <section className="survey-shell">

                {/* =========================
                    Header
                ========================== */}

                <header className="survey-header">

                    <div className="survey-header-content">

                        <span className="survey-header-icon">
                            <MdStar />
                        </span>

                        <div>

                            <p className="survey-eyebrow">
                                کلینیک تصویربرداری عارف
                            </p>

                            <h1>
                                {meta.title}
                            </h1>

                            <p className="survey-header-description">
                                تجربه شما به ما کمک می‌کند
                                خدمات بهتری ارائه کنیم.
                            </p>

                        </div>

                    </div>


                    <span className="survey-time-badge">
                        کمتر از ۱ دقیقه
                    </span>

                </header>


                {/* =========================
                    Progress
                ========================== */}

                <div className="survey-progress-section">

                    <div className="survey-progress-info">

                        <span>
                            میزان تکمیل نظرسنجی
                        </span>

                        <strong>
                            {answeredCount} از {criteria.length}
                        </strong>

                    </div>


                    <div className="survey-progress-track">

                        <div
                            className="survey-progress-value"
                            style={{
                                width: `${progress}%`,
                            }}
                        />

                    </div>

                </div>


                <form
                    onSubmit={submit}
                    className="survey-form"
                >

                    {/* =========================
                        Patient information
                    ========================== */}

                    <section className="survey-section">

                        <div className="survey-section-heading">

                            <span className="survey-section-number">
                                ۱
                            </span>

                            <div>

                                <strong>
                                    اطلاعات پذیرش
                                </strong>

                                <p>
                                    اطلاعات بیمار را بررسی کنید.
                                </p>

                            </div>

                        </div>


                        <div className="survey-form-grid">

                            <label className="survey-field">

                                <span>
                                    شماره پذیرش
                                </span>

                                <div className="survey-input-wrap">

                                    {(inviteToken || receptionFromUrl)
                                        ? <FiLock />
                                        : <FiUser />
                                    }

                                    <input
                                        inputMode="numeric"

                                        value={
                                            receptionNumber
                                        }

                                        readOnly={Boolean(
                                            inviteToken ||
                                            receptionFromUrl
                                        )}

                                        onChange={event => {
                                            setReceptionNumber(
                                                event.target.value
                                                    .replace(/\D/g, '')
                                            );
                                        }}

                                        placeholder="مثلاً 140512345"
                                    />

                                </div>

                            </label>


                            <label className="survey-field">

                                <span>
                                    شماره موبایل بیمار
                                </span>

                                <div className="survey-input-wrap">

                                    {(inviteToken || phoneFromUrl)
                                        ? <FiLock />
                                        : <FiPhone />
                                    }

                                    <input
                                        className="ltr-input"

                                        dir="ltr"

                                        inputMode="numeric"

                                        maxLength={11}

                                        value={
                                            phoneNumber
                                        }

                                        readOnly={Boolean(
                                            inviteToken ||
                                            phoneFromUrl
                                        )}

                                        onChange={event => {
                                            setPhoneNumber(
                                                event.target.value
                                                    .replace(/\D/g, '')
                                                    .slice(0, 11)
                                            );
                                        }}

                                        placeholder="09123456789"
                                    />

                                </div>

                            </label>

                        </div>

                    </section>


                    {/* =========================
                        Rating
                    ========================== */}

                    <section className="survey-section survey-rating-section">

                        <div className="survey-section-heading">

                            <span className="survey-section-number">
                                ۲
                            </span>

                            <div>

                                <strong>
                                    ارزیابی {meta.legend}
                                </strong>

                                <p>
                                    برای هر مورد از ۱ تا ۵ ستاره
                                    انتخاب کنید.
                                </p>

                            </div>

                        </div>


                        <div className="survey-rating-guide">

                            <span>
                                عالی
                            </span>

                            <div />

                            <span>
                                خیلی ضعیف
                            </span>

                        </div>


                        <div className="survey-criteria-list">

                            {criteria.map(
                                (item, index) => {

                                    const selectedRating =
                                        ratings[item.key] || 0;

                                    return (
                                        <div
                                            key={item.key}

                                            className={
                                                `survey-criterion-card ${
                                                    selectedRating
                                                        ? 'survey-criterion-completed'
                                                        : ''
                                                }`
                                            }
                                        >

                                            <div className="survey-criterion-header">

                                                <div className="survey-criterion-title">

                                                    <span>
                                                        {index + 1}
                                                    </span>

                                                    <strong>
                                                        {item.label}
                                                    </strong>

                                                </div>


                                                {selectedRating > 0 && (
                                                    <span className="survey-rating-result">

                                                        {
                                                            ratingLabels[
                                                                selectedRating
                                                            ]
                                                        }

                                                    </span>
                                                )}

                                            </div>


                                            <div
                                                className="survey-stars survey-stars-row"
                                                dir="ltr"
                                            >

                                                {[1, 2, 3, 4, 5].map(
                                                    value => {

                                                        const selected =
                                                            value <=
                                                            selectedRating;

                                                        return (
                                                            <button
                                                                type="button"

                                                                key={
                                                                    value
                                                                }

                                                                className={
                                                                    selected
                                                                        ? 'selected'
                                                                        : ''
                                                                }

                                                                onClick={() =>
                                                                    selectRating(
                                                                        item.key,
                                                                        value
                                                                    )
                                                                }

                                                                aria-label={
                                                                    `${value} امتیاز برای ${item.label}`
                                                                }
                                                            >

                                                                {selected
                                                                    ? <MdStar />
                                                                    : <MdStarBorder />
                                                                }

                                                            </button>
                                                        );
                                                    }
                                                )}

                                            </div>

                                        </div>
                                    );
                                }
                            )}

                        </div>


                        {allRated && (

                            <div className="survey-all-completed">

                                <FiCheck />

                                <span>
                                    به همه موارد امتیاز دادید.
                                </span>

                            </div>

                        )}

                    </section>


                    {/* =========================
                        Description
                    ========================== */}

                    <section className="survey-section">

                        <div className="survey-section-heading">

                            <span className="survey-section-number">
                                ۳
                            </span>

                            <div>

                                <strong>
                                    نظر شما
                                </strong>

                                <p>
                                    اگر توضیح یا پیشنهادی دارید
                                    برای ما بنویسید.
                                </p>

                            </div>

                        </div>


                        <label className="survey-field survey-field-full">

                            <div className="survey-textarea-wrap">

                                <FiMessageSquare />

                                <textarea
                                    maxLength={2000}

                                    rows={4}

                                    value={
                                        description
                                    }

                                    onChange={event =>
                                        setDescription(
                                            event.target.value
                                        )
                                    }

                                    placeholder="پیشنهاد، انتقاد یا تجربه خود را بنویسید..."
                                />

                            </div>


                            <div className="survey-character-count">

                                <span>
                                    اختیاری
                                </span>

                                <span>
                                    {description.length} / 2000
                                </span>

                            </div>

                        </label>

                    </section>


                    {/* =========================
                        Error
                    ========================== */}

                    {error && (

                        <div
                            className="survey-error"
                            role="alert"
                        >
                            {error}
                        </div>

                    )}
                    {error && !inviteToken && <button type="button" className="survey-secondary-button"
                        disabled={submitting} onClick={() => setReload(value => value + 1)}>
                        دریافت دوباره فرم
                    </button>}


                    {/* =========================
                        Submit
                    ========================== */}

                    <button
                        type="submit"

                        className="survey-primary-button"

                        disabled={
                            submitting ||
                            !allRated
                        }
                    >

                        {submitting ? (
                            'در حال ثبت نظر...'
                        ) : (
                            <>
                                <span>
                                    ثبت نهایی نظر
                                </span>

                                <FiSend />
                            </>
                        )}

                    </button>


                    <p className="survey-privacy">

                        <FiLock />

                        <span>
                            اطلاعات شما فقط برای بررسی
                            کیفیت خدمات استفاده می‌شود.
                        </span>

                    </p>

                </form>

            </section>

        </main>
    );
}
